import queryString from 'querystring';

import React, { useMemo } from 'react';

import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import memoize from 'lodash.memoize';
import { bindActionCreators } from 'redux';

import { ThunkActionCreator } from '@ttstr/actions';
import { Article, Articles, Cart, Product, Variant } from '@ttstr/api';

export const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
export const BIC_PATTERN = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/;
export const IBAN_PATTERN = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{4}[0-9]{7}([A-Z0-9]?){0,16}$/;

export type Uncasted<T> = {
  [key in keyof T]?: any;
};

export const filter = {
  keepAll: () => true,
  byKey: <T extends any>(key: string) => {
    const keyParts = key.split('.');
    return (item: T) => !!keyParts.reduce((i: any, k) => i && i[k], item);
  },
  byFulltextMatch(needle: string) {
    const lowerCaseNeedle = needle.trim().toLowerCase();
    const matcher = (item: any): boolean => {
      if (item === undefined) return false;
      if (item === null) return false;
      if (typeof item === 'string') {
        return item.trim().toLowerCase().includes(lowerCaseNeedle);
      }
      if (Array.isArray(item)) return item.some((i) => matcher(i));
      if (typeof item === 'object') {
        return Object.values(item).some((value) => matcher(value));
      }
      return false;
    };
    return matcher;
  },
};

export const sort = {
  byCallback: <T = any>(cb: (x: T) => any, reverse = false) => {
    const inverter = reverse ? -1 : +1;
    return (a: T, b: T) => {
      const aKey = cb(a);
      const bKey = cb(b);
      if (aKey > bKey) return +1 * inverter;
      if (aKey < bKey) return -1 * inverter;
      return 0;
    };
  },
  byKey: <T = any>(key: string, reverse = false) => {
    const keyParts = key.split('.');
    const inverter = reverse ? -1 : +1;
    return (a: T, b: T) => {
      const aKey = keyParts.reduce((i: any, k) => i && i[k], a);
      const bKey = keyParts.reduce((i: any, k) => i && i[k], b);
      if (aKey > bKey) return +1 * inverter;
      if (aKey < bKey) return -1 * inverter;
      return 0;
    };
  },
};

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Tool to create an n-length array
 * @param times Number of array elements
 * @returns An 1-based iteration of the elements
 */
export const repeat = (times: number) => Array.from(Array(times)).map((_, i) => i + 1);

export const productDetailLink = (product: Article, { canonicalUrl = '' } = {}) => {
  const slug = product.ptitle.replace('-wbr-', '') ?? '';
  if (canonicalUrl) return `${canonicalUrl}/product/${product.id}/${slug}`;
  return `/product/${product.id}/${slug}`;
};

export const getDateRangeForProducts = memoize((products: Articles) => {
  if (!products.length) return null;
  return products.reduce(
    ({ min, max }, product) => {
      if (!product.valid_start_on) return { min, max };
      const current = new Date(product.valid_start_on);
      return {
        min: min > current ? current : min,
        max: max < current ? current : max,
      };
    },
    { min: new Date(), max: new Date() }
  );
});

export function isNumeric(value: any) {
  return !Number.isNaN(Number(value)) && Number.isFinite(value);
}

export function leftMerge<T extends { [key: string]: any }>(left: T, ...rest: Partial<T>[]): T {
  if (!left) return left;
  if (!rest || !rest.length) return left;
  const reverseRest = rest.filter(Boolean).reverse();
  return Object.entries(left).reduce((acc, [key, value]) => {
    const foundItem = reverseRest.find((candidate) => {
      return key in candidate && candidate[key] != null;
    });
    const mergedValue = foundItem ? foundItem[key] : value;

    if (value instanceof Object && mergedValue instanceof Object) {
      return {
        ...acc,
        [key]: leftMerge(value, mergedValue),
      };
    }

    return {
      ...acc,
      [key]: mergedValue,
    };
  }, {} as T);
}

/**
 * @see https://react-redux.js.org/next/api/hooks#recipe-useshallowequalselector
 * @param selector
 */
export function useShallowEqualSelector<TState, TSelected>(selector: (state: TState) => TSelected) {
  return useSelector<TState, Readonly<TSelected>>(selector, shallowEqual);
}

/**
 *
 * @param actions key-value pair of actions where the key is the name of the action and the value the actual function
 * @param dependencies optional list of dependencies that'll trigger React.useMemo to rerender
 */
export function useActions<T extends { [key: string]: ThunkActionCreator }>(actions: T, dependencies?: any[]): any {
  const dispatch = useDispatch();
  return useMemo(() => bindActionCreators(actions, dispatch), dependencies ? [dispatch, ...dependencies] : [dispatch]);
}

/**
 * Helper that returns a ref indicating if the current component is still mounted
 */
export function useMounted() {
  const isMounted = React.useRef(false);
  React.useEffect(() => {
    isMounted.current = true;
    return () => (isMounted.current = false);
  }, []);
  return isMounted;
}

type CallbackRef<T> = (instance: T) => void;

export function useCombinedRefs<T extends HTMLElement, A extends (React.MutableRefObject<T> | CallbackRef<T>)[]>(
  ...refs: A
): CallbackRef<T> {
  return React.useCallback((ref: T) => {
    refs.forEach((r) => {
      if (typeof r === 'function') (r as any)(ref);
      else if (r !== null && typeof r === 'object' && 'current' in r) r.current = ref;
    });
  }, refs);
}

export function getDefaults<T extends { [key: string]: any }>(src: Partial<T>, defaults: T, prefix = ''): T {
  return Object.keys(defaults).reduce((acc, key) => {
    const path = prefix + key;
    if (!src) return acc;
    if (src[key] == null) return acc;
    if (src[key] === '') return acc;
    if (src[key] === defaults[key]) return acc;
    if (typeof defaults[key] === 'object' && typeof src[key] === 'object') {
      const sub = getDefaults(src[key], defaults[key]);
      if (Object.keys(defaults).length) {
        return {
          ...acc,
          [path]: sub,
        };
      }
      return acc;
    }
    return {
      ...acc,
      [path]: src[key],
    };
  }, {} as T);
}

export function useQueryString() {
  const location = useLocation();

  const qs = React.useMemo(() => {
    return queryString.parse(location.search.substring(1));
  }, [location.search]);

  return qs;
}

export function useToggle(initial = false): [boolean, () => void, React.Dispatch<React.SetStateAction<boolean>>] {
  const [state, setState] = React.useState(initial);

  const toggleState = () =>
    setState((previous) => {
      return !previous;
    });
  return [state, toggleState, setState];
}

/**
 * beware: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/RegExp/test
 * > as with exec() (or in combination with it), test() called multiple times on the same global regular expression instance will advance past the previous match.
 * > As long as test() returns true, lastIndex will not reset—even when testing a different string! 
 *  
 * e.g. don't use the global modifier.
 */

const ABSOLUTE_URL_MATCHER = /^(([a-z+_-]+:)|\/\/)/i;

/**
 * Returns true if the URL is absolute
 *
 * Example absolute URLs:
 * * `mailto:test@absolute.url`
 * * `https://domain.tld/path/to/file.ext`
 * * `//keep-protocol.tld`
 *
 * Example relative URLs (which will not match):
 * * `/absolute-path-in-the-same-domain/file.ext`
 * * `relative/path/in/the/same/domain?query=string`
 *
 * @param url string to test
 * @see ABSOLUTE_URL_MATCHER const for more details
 */
export function isAbsoluteURL(url: string) {
  return ABSOLUTE_URL_MATCHER.test(url);
}

export function trackMetaAndTiktokEvent( eventName: string, eventData: any, specialEventIdName?: string, customEventId?: string) {
  if("ttq" in window){
    window.ttq.track(eventName, eventData);
  }
  if("fbq" in window){
    const eventId = customEventId || crypto.randomUUID();
    sessionStorage.setItem(specialEventIdName || "eventId", eventId);
    window.fbq('track', eventName, eventData, {event_id: eventId});
  }
}
export function trackAddToCartEvent(
  product: Product,
  variant: Variant,
  count: number,
  customEventId?: string
) {
  if("fbq" in window || "ttq" in window) {
    const eventName = 'AddToCart';
    const defaultCurrency = 'EUR';
    const totalPrice = Number(variant.total_price) * count;
    const contentType = 'product';
    const desc = product.description || variant.title || product.ptitle;
    const contentIds = [String(product.id || variant.article_id || variant.id)];
    const eventData = {
      content_type: contentType,
      quantity: count,
      value: totalPrice,
      currency: defaultCurrency,
      description: desc,
      content_ids: contentIds,
      content_id: contentIds,
    };

    trackMetaAndTiktokEvent(eventName, eventData, "addToCartEventId", customEventId);
  }
}

export function trackViewContentEvent(product: any, title: string, customEventId?: string){
  if("fbq" in window || "ttq" in window) {
    const shopsArray = ["Kopf & Steine Shop"];
    if(shopsArray.includes(title)){
      const eventName = 'ViewContent';
      const price = product.min_price || product.max_price || 0;
      const contentType = 'product';
      const contentName = product.title || product.ptitle || '';
      const contentIds = [String(product.id)];
      const defaultCurrency = 'EUR';

      const contentsElement = [
        {
          id: String(product.id),
          quantity: 1,
          item_price: price,
        },
      ]
      const eventData = {
        content_type: contentType,
        content_name: contentName,
        content_ids: contentIds,
        contents: contentsElement,
        content_id: contentIds[0],
        value: price,
        currency: defaultCurrency,
      };
      trackMetaAndTiktokEvent(eventName, eventData, "viewContentEventId", customEventId);
    }
  }

}

export function trackPurchaseEvent(shopTitle: string, cart: Cart, customEventId?: string){
  if("fbq" in window || "ttq" in window) {
    const shopsArray = ["Kopf & Steine Shop", "PAULA HARTMANN - Tickets & Merchandise"]
    if (cart && shopsArray.includes(shopTitle)) {
      const eventName = 'Purchase';
      const contentsArray = [];
      const price = cart.price_after_coupons || cart.total_value || 0;
      const contentType = 'product';
      for (const item of cart.items || []) {
        if (item.variant.is_fee) continue;
        if (item.variant?.article?.id) {
          contentsArray.push({
            id: String(item.variant.article.id),
            quantity: item.quantity,
          });
        }
      }
      const eventData = {
        contents: contentsArray,
        value: price,
        currency: 'EUR',
        content_type: contentType,
      };
      trackMetaAndTiktokEvent(eventName, eventData, "purchaseEventId", customEventId);
    }
  }
}
export function getPpClientId(clientId: any) {
  return (process.env.NODE_ENV === 'development') ? "BAAugznqYbyxne-41twQLBnj7EVgVlHhWposK5ygpFvSD2_QLS1j65r4CJipjl0q2FqqmEHiNxqKcBKDKw" : clientId;
}
export function getPpBnCode(bnCode: any) {
  return (process.env.NODE_ENV === 'development') ? "tickettoaster_Cart_PPCP" : bnCode;
}
